import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { Location, AtlasMapBlock, AtlasTool } from '../types';
import { AtlasService } from '../lib/atlasService';
import { AtlasMapService } from '../lib/atlasMapService';

// Constants
const BLOCK_SIZE = 200;

interface AtlasContextType {
    // Current idea context
    currentIdeaId: string | null;
    setCurrentIdeaId: (id: string | null) => void;

    // Locations
    locations: Location[];
    placedLocations: Location[];
    unplacedLocations: Location[];
    loadLocations: (ideaId: string) => Promise<void>;
    createLocation: (name: string, description: string, type?: string) => Promise<Location | null>;
    updateLocation: (id: string, updates: Partial<Location>) => Promise<void>;
    deleteLocation: (id: string) => Promise<void>;
    placeLocation: (id: string, x: number, y: number, imageUrl: string) => Promise<void>;
    unplaceLocation: (id: string) => Promise<void>;

    // Map Blocks
    mapBlocks: AtlasMapBlock[];
    loadMapBlocks: (ideaId: string) => Promise<void>;
    generateMapBlock: (x: number, y: number, adjacentBlocks: (Location | AtlasMapBlock)[]) => Promise<AtlasMapBlock | null>;
    deleteMapBlock: (id: string) => Promise<void>;

    // Canvas State
    tool: AtlasTool;
    setTool: (tool: AtlasTool) => void;
    selectedLocationId: string | null;
    setSelectedLocationId: (id: string | null) => void;
    selectedMapBlockId: string | null;
    setSelectedMapBlockId: (id: string | null) => void;

    // Viewport State
    viewportState: {
        positionX: number;
        positionY: number;
        scale: number;
    };
    setViewportState: (state: { positionX: number; positionY: number; scale: number }) => void;

    // Modal State
    isCreateModalOpen: boolean;
    setIsCreateModalOpen: (open: boolean) => void;
    pendingPlacement: { x: number; y: number } | null;
    setPendingPlacement: (placement: { x: number; y: number } | null) => void;

    // Loading States
    isLoading: boolean;
    isGenerating: boolean;
}

const AtlasContext = createContext<AtlasContextType | undefined>(undefined);

export const useAtlas = () => {
    const context = useContext(AtlasContext);
    if (!context) {
        throw new Error('useAtlas must be used within an AtlasProvider');
    }
    return context;
};

export const AtlasProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Current idea
    const [currentIdeaId, setCurrentIdeaId] = useState<string | null>(null);

    // Data
    const [locations, setLocations] = useState<Location[]>([]);
    const [mapBlocks, setMapBlocks] = useState<AtlasMapBlock[]>([]);

    // Canvas state
    const [tool, setTool] = useState<AtlasTool>('select');
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [selectedMapBlockId, setSelectedMapBlockId] = useState<string | null>(null);

    // Viewport
    const [viewportState, setViewportState] = useState({
        positionX: 0,
        positionY: 0,
        scale: 1
    });

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [pendingPlacement, setPendingPlacement] = useState<{ x: number; y: number } | null>(null);

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    // Computed: placed vs unplaced locations
    const placedLocations = useMemo(() =>
        locations.filter(loc => loc.canvas_x != null && loc.canvas_y != null),
        [locations]
    );

    const unplacedLocations = useMemo(() =>
        locations.filter(loc => loc.canvas_x == null || loc.canvas_y == null),
        [locations]
    );

    // Load locations for an idea
    const loadLocations = useCallback(async (ideaId: string) => {
        setIsLoading(true);
        try {
            const locs = await AtlasService.getLocations(ideaId);
            setLocations(locs);
        } catch (error) {
            console.error('Failed to load locations:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Create a new location (text-only, unplaced)
    const createLocation = useCallback(async (name: string, description: string, type: string = 'exterior'): Promise<Location | null> => {
        if (!currentIdeaId) return null;
        try {
            const location = await AtlasService.createLocation(currentIdeaId, name, description, type);
            if (location) {
                setLocations(prev => [...prev, location]);
            }
            return location;
        } catch (error) {
            console.error('Failed to create location:', error);
            return null;
        }
    }, [currentIdeaId]);

    // Update a location
    const updateLocation = useCallback(async (id: string, updates: Partial<Location>) => {
        try {
            await AtlasService.updateLocation(id, updates);
            setLocations(prev => prev.map(loc =>
                loc.id === id ? { ...loc, ...updates } : loc
            ));
        } catch (error) {
            console.error('Failed to update location:', error);
        }
    }, []);

    // Delete a location
    const deleteLocation = useCallback(async (id: string) => {
        try {
            await AtlasService.deleteLocation(id);
            setLocations(prev => prev.filter(loc => loc.id !== id));
            if (selectedLocationId === id) {
                setSelectedLocationId(null);
            }
        } catch (error) {
            console.error('Failed to delete location:', error);
        }
    }, [selectedLocationId]);

    // Place a location on the canvas (with generated image)
    const placeLocation = useCallback(async (id: string, x: number, y: number, imageUrl: string) => {
        try {
            await AtlasService.updateLocation(id, {
                canvas_x: x,
                canvas_y: y,
                canvas_width: BLOCK_SIZE,
                canvas_height: BLOCK_SIZE,
                atlas_image_url: imageUrl
            });
            setLocations(prev => prev.map(loc =>
                loc.id === id ? {
                    ...loc,
                    canvas_x: x,
                    canvas_y: y,
                    canvas_width: BLOCK_SIZE,
                    canvas_height: BLOCK_SIZE,
                    atlas_image_url: imageUrl
                } : loc
            ));
            console.log('📍 Location placed:', { id, x, y, imageUrl });
        } catch (error) {
            console.error('Failed to place location:', error);
        }
    }, []);

    // Remove a location from canvas (keeps the text data, moves to unplaced)
    const unplaceLocation = useCallback(async (id: string) => {
        try {
            await AtlasService.removeFromCanvas(id);
            setLocations(prev => prev.map(loc =>
                loc.id === id ? {
                    ...loc,
                    canvas_x: undefined,
                    canvas_y: undefined,
                    canvas_width: undefined,
                    canvas_height: undefined,
                    atlas_image_url: undefined
                } : loc
            ));
            if (selectedLocationId === id) {
                setSelectedLocationId(null);
            }
            console.log('📍 Location unplaced:', id);
        } catch (error) {
            console.error('Failed to unplace location:', error);
        }
    }, [selectedLocationId]);

    // Load map blocks for an idea
    const loadMapBlocks = useCallback(async (ideaId: string) => {
        try {
            const blocks = await AtlasMapService.getMapBlocks(ideaId);
            setMapBlocks(blocks);
        } catch (error) {
            console.error('Failed to load map blocks:', error);
        }
    }, []);

    // Generate a new map block using img2img
    const generateMapBlock = useCallback(async (
        x: number,
        y: number,
        adjacentBlocks: (Location | AtlasMapBlock)[]
    ): Promise<AtlasMapBlock | null> => {
        if (!currentIdeaId) return null;
        setIsGenerating(true);
        try {
            const block = await AtlasMapService.generateBlock(currentIdeaId, x, y, adjacentBlocks);
            if (block) {
                setMapBlocks(prev => [...prev, block]);
            }
            return block;
        } catch (error) {
            console.error('Failed to generate map block:', error);
            return null;
        } finally {
            setIsGenerating(false);
        }
    }, [currentIdeaId]);

    // Delete a map block
    const deleteMapBlock = useCallback(async (id: string) => {
        try {
            await AtlasMapService.deleteMapBlock(id);
            setMapBlocks(prev => prev.filter(block => block.id !== id));
            if (selectedMapBlockId === id) {
                setSelectedMapBlockId(null);
            }
        } catch (error) {
            console.error('Failed to delete map block:', error);
        }
    }, [selectedMapBlockId]);

    const value: AtlasContextType = {
        currentIdeaId,
        setCurrentIdeaId,
        locations,
        placedLocations,
        unplacedLocations,
        loadLocations,
        createLocation,
        updateLocation,
        deleteLocation,
        placeLocation,
        unplaceLocation,
        mapBlocks,
        loadMapBlocks,
        generateMapBlock,
        deleteMapBlock,
        tool,
        setTool,
        selectedLocationId,
        setSelectedLocationId,
        selectedMapBlockId,
        setSelectedMapBlockId,
        viewportState,
        setViewportState,
        isCreateModalOpen,
        setIsCreateModalOpen,
        pendingPlacement,
        setPendingPlacement,
        isLoading,
        isGenerating
    };

    return (
        <AtlasContext.Provider value={value}>
            {children}
        </AtlasContext.Provider>
    );
};
